import { ToastContext } from "context/ToastContext";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useState, useMemo, useContext, useCallback, useEffect } from "react";
import styled from "styled-components";

import {
  useGenerateBaseRateFileMutation,
  useGetIppsStandardizedAmountsQuery,
  useGetLoadedPsfQuartersQuery,
  useGetOverrideItemsQuery,
  useSearchAscWageIndexQuery,
  useSearchGpciLocalitiesQuery,
  useSearchIppsImpactHospitalsQuery,
} from "../../../store/queries/baseRateFiles";
import {
  IASCWageIndexEntry,
  IBaseRateFile,
  ICmsPsfOverrides,
  ICmsToggleConfig,
  IGpciLocality,
  IIppsImpactHospital,
  ISourceDataStatus,
} from "../../../types/baseRateFile";
import {
  DEFAULT_CMS_TOGGLES,
  DMEPOS_STATE_OPTIONS,
  FEE_SCHEDULE_TYPE_OPTIONS,
  IPPS_SCENARIO_OPTIONS,
  US_STATE_OPTIONS,
  FeeScheduleType,
  FeeScheduleTypeValue,
} from "../utils/constants";
import HospitalInpatientCmsFields from "./HospitalInpatientCmsFields";
import { getYearOptions } from "../utils/helpers";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--surface-border);
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1rem;

  > div {
    flex: 1;
  }
`;

const StyledMultiSelectWrapper = styled.div`
  .p-multiselect {
    .p-multiselect-label-container {
      flex-wrap: wrap;
    }
    .p-multiselect-label {
      white-space: normal;
    }
    .p-multiselect-token {
      margin: 0.125rem;
    }
  }
`;

type OPPSSourceKey = "addendumB" | "clfs" | "asp" | "dmepos" | "pprvu";

const OPPS_SOURCE_DEFAULTS: Record<OPPSSourceKey, boolean> = {
  addendumB: true,
  clfs: true,
  asp: true,
  dmepos: true,
  pprvu: true,
};

interface ICreateBaseRateFileDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  sourceDataStatus: ISourceDataStatus | null;
  /** Account-scoped list from the parent; used to pre-check display name uniqueness. */
  existingBaseRateFiles?: IBaseRateFile[];
}

// Map fee schedule types to required source data keys
const getRequiredSourcesForType = (
  type: FeeScheduleTypeValue,
  includedSources?: Record<OPPSSourceKey, boolean>
): (keyof ISourceDataStatus)[] => {
  switch (type) {
    case FeeScheduleType.HOSPITAL_INPATIENT:
      return ["ipps", "ippsStdAmounts"];
    case FeeScheduleType.HOSPITAL_INPATIENT_CMS:
      // Pricer reads DRG weights from BaseRateIpps, std amounts from BaseRateIppsStdAmounts,
      // and per-hospital fields from BaseRatePsfInpatient (the API-fetched source).
      // IPPS Impact File is also needed for hospital display names — PSF API doesn't
      // include them. Once we add a dedicated hospital-name source this can drop.
      return ["ipps", "ippsStdAmounts", "ippsPsfInpatient", "ippsImpact"];
    case FeeScheduleType.HOSPITAL_OPPS_NATIONAL: {
      const allSources: OPPSSourceKey[] = [
        "addendumB",
        "clfs",
        "asp",
        "dmepos",
        "pprvu",
      ];
      if (includedSources) {
        return allSources.filter((s) => includedSources[s]);
      }
      return allSources;
    }
    case FeeScheduleType.ASC:
      return ["addendumAA"];
    case FeeScheduleType.PHYSICIAN_FACILITY:
    case FeeScheduleType.PHYSICIAN_NON_FACILITY:
      return ["pprvu"];
    default:
      return ["addendumB", "addendumAA", "clfs", "asp", "dmepos", "pprvu"];
  }
};

function displayNameLabelTakenByAnotherLogicalFile(
  rows: IBaseRateFile[],
  inputDisplayName: string
): IBaseRateFile | undefined {
  const label = inputDisplayName.trim();
  if (!label) return undefined;
  return rows.find((f) => f.displayName.trim() === label);
}

function CreateBaseRateFileDialog({
  visible,
  onHide,
  onSuccess,
  sourceDataStatus,
  existingBaseRateFiles = [],
}: ICreateBaseRateFileDialogProps) {
  const { current: toastElement } = useContext(ToastContext);

  // Basic fields
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [type, setType] = useState<FeeScheduleTypeValue>(
    FeeScheduleType.HOSPITAL_OPPS_NATIONAL
  );

  // Common adjustments
  const [applySequester, setApplySequester] = useState(false);

  // OPPS Wage Adjusted / ASC / Hospital Inpatient fields
  const [wageIndex, setWageIndex] = useState<number | null>(null);
  const [gaf, setGaf] = useState<number | null>(1.0);
  const [ippsScenario, setIppsScenario] = useState<string>("quality_ehr");

  // Hospital Inpatient — IME / DSH / UCP from IPPS Impact (editable; sent on generate)
  const [ippsTchop, setIppsTchop] = useState<number>(0);
  const [ippsTchcp, setIppsTchcp] = useState<number>(0);
  const [ippsDshopp, setIppsDshopp] = useState<number>(0);
  const [ippsDshcpp, setIppsDshcpp] = useState<number>(0);
  const [ippsUcpPerClaim, setIppsUcpPerClaim] = useState<number>(0);

  // Hospital search fields (for IPPS Impact File lookup)
  const [hospitalSearchQuery, setHospitalSearchQuery] = useState<string>("");
  const [selectedHospital, setSelectedHospital] =
    useState<IIppsImpactHospital | null>(null);
  const [showHospitalSearch, setShowHospitalSearch] = useState<boolean>(false);

  // Locality search fields (for GPCI lookup)
  const [localitySearchQuery, setLocalitySearchQuery] = useState<string>("");
  const [selectedLocality, setSelectedLocality] =
    useState<IGpciLocality | null>(null);
  const [showLocalitySearch, setShowLocalitySearch] = useState<boolean>(false);

  // ASC Wage Index search fields
  const [ascState, setAscState] = useState<string>("");
  const [ascCountySearchQuery, setAscCountySearchQuery] = useState<string>("");
  const [selectedAscEntry, setSelectedAscEntry] =
    useState<IASCWageIndexEntry | null>(null);
  const [showAscSearch, setShowAscSearch] = useState<boolean>(false);

  // Physician GPCI fields
  const [gpciPw, setGpciPw] = useState<number | null>(1.0);
  const [gpciPe, setGpciPe] = useState<number | null>(1.0);
  const [gpciMp, setGpciMp] = useState<number | null>(1.0);

  // Common source adjustments (apply to all types)
  const [dmeposState, setDmeposState] = useState<string>("KS_NR");
  const [dmeposPercent, setDmeposPercent] = useState<number | null>(100);
  const [aspPercent, setAspPercent] = useState<number | null>(110);
  const [clfsPercent, setClfsPercent] = useState<number | null>(100);
  const [pprvuPercent, setPprvuPercent] = useState<number | null>(100);

  // Hospital Inpatient CMS (PSF-driven) state
  const [cmsProviderCcn, setCmsProviderCcn] = useState<string>("");
  /** Calendar quarter (1-4) of the PSF snapshot. null until the operator picks
   *  one from the modal's dropdown. */
  const [cmsQuarter, setCmsQuarter] = useState<number | null>(null);
  const [cmsToggles, setCmsToggles] =
    useState<ICmsToggleConfig>(DEFAULT_CMS_TOGGLES);
  const [cmsPsfOverrides, setCmsPsfOverrides] = useState<ICmsPsfOverrides>({});

  // OPPS source selection (which sources to include in generation)
  const [includedSources, setIncludedSources] =
    useState<Record<OPPSSourceKey, boolean>>(OPPS_SOURCE_DEFAULTS);

  const toggleSource = useCallback((key: OPPSSourceKey) => {
    setIncludedSources((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const atLeastOneSourceSelected = useMemo(
    () =>
      type !== FeeScheduleType.HOSPITAL_OPPS_NATIONAL ||
      Object.values(includedSources).some(Boolean),
    [type, includedSources]
  );

  // Selected override rules
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>([]);

  const [generateBaseRateFile, { isLoading: isCreating }] =
    useGenerateBaseRateFileMutation();

  // Loaded PSF (year, quarter) snapshots — drives the Quarter dropdown in the
  // Hospital Inpatient CMS section. We only query when CMS is the chosen type.
  const { data: loadedQuartersResponse } = useGetLoadedPsfQuartersQuery(
    undefined,
    { skip: type !== FeeScheduleType.HOSPITAL_INPATIENT_CMS }
  );
  const availableQuarters = useMemo(
    () => loadedQuartersResponse?.data ?? [],
    [loadedQuartersResponse]
  );

  // Default the quarter to the largest one available for the picked year
  // whenever year or available quarters change. Clear it when no quarter
  // exists for that year so the validation pinches the operator into action.
  useEffect(() => {
    if (type !== FeeScheduleType.HOSPITAL_INPATIENT_CMS) return;
    const forYear = availableQuarters
      .filter((q) => q.year === year)
      .map((q) => q.quarter)
      .sort((a, b) => b - a);
    if (forYear.length === 0) {
      if (cmsQuarter !== null) setCmsQuarter(null);
      return;
    }
    if (cmsQuarter === null || !forYear.includes(cmsQuarter)) {
      setCmsQuarter(forYear[0]);
    }
  }, [type, year, availableQuarters, cmsQuarter]);

  // Fetch override items (configs)
  const { data: overrideItemsData } = useGetOverrideItemsQuery();
  const overrideItems = overrideItemsData?.data || [];

  // Create options for MultiSelect from enabled override items matching the selected type
  const overrideRuleOptions = useMemo(() => {
    return overrideItems
      .filter((item) => item.isEnabled && item.feeScheduleType === type)
      .map((item) => ({
        label: `${item.name}`,
        value: item.id,
      }));
  }, [overrideItems, type]);

  // Hospital search (only for Hospital types with IPPS Impact File)
  const shouldEnableHospitalSearch =
    showHospitalSearch &&
    hospitalSearchQuery.length >= 2 &&
    (type === FeeScheduleType.HOSPITAL_INPATIENT ||
      type === FeeScheduleType.HOSPITAL_OPPS_NATIONAL);

  const { data: hospitalSearchData } = useSearchIppsImpactHospitalsQuery(
    { year, q: hospitalSearchQuery },
    { skip: !shouldEnableHospitalSearch }
  );
  const hospitalSearchResults = hospitalSearchData?.data || [];

  // Check if IPPS Impact File is uploaded for current year
  const hasIppsImpactFile = useMemo(() => {
    if (!sourceDataStatus?.ippsImpact) return false;
    return sourceDataStatus.ippsImpact.some((info) => info.year === year);
  }, [sourceDataStatus, year]);

  // Locality search (only for Physician types with GPCI file)
  const shouldEnableLocalitySearch =
    showLocalitySearch &&
    localitySearchQuery.length >= 2 &&
    (type === FeeScheduleType.PHYSICIAN_FACILITY ||
      type === FeeScheduleType.PHYSICIAN_NON_FACILITY);

  const { data: localitySearchData } = useSearchGpciLocalitiesQuery(
    { year, q: localitySearchQuery },
    { skip: !shouldEnableLocalitySearch }
  );
  const localitySearchResults = localitySearchData?.data || [];

  // Check if GPCI file is uploaded for current year
  const hasGpciFile = useMemo(() => {
    if (!sourceDataStatus?.gpci) return false;
    return sourceDataStatus.gpci.some((info) => info.year === year);
  }, [sourceDataStatus, year]);

  // ASC Wage Index search (only for ASC type)
  const shouldEnableAscSearch =
    showAscSearch &&
    ascState.length === 2 &&
    ascCountySearchQuery.length >= 2 &&
    type === FeeScheduleType.ASC;

  const { data: ascSearchData } = useSearchAscWageIndexQuery(
    { year, state: ascState, query: ascCountySearchQuery },
    { skip: !shouldEnableAscSearch }
  );
  const ascSearchResults = ascSearchData?.data || [];

  // Check if ASC Wage Index file is uploaded for current year
  const hasAscWageIndexFile = useMemo(() => {
    if (!sourceDataStatus?.ascWageIndex) return false;
    return sourceDataStatus.ascWageIndex.some((info) => info.year === year);
  }, [sourceDataStatus, year]);

  // Check if IPPS Tables 1A-1E (Standardized Amounts) is uploaded for current year
  const hasIppsStdAmountsFile = useMemo(() => {
    if (!sourceDataStatus?.ippsStdAmounts) return false;
    return sourceDataStatus.ippsStdAmounts.some((info) => info.year === year);
  }, [sourceDataStatus, year]);

  // IPPS Standardized Amounts query (for Hospital Inpatient)
  // Requires: year, tableType (from selectedHospital), scenario, and uploaded source file
  const shouldFetchStandardizedAmounts =
    type === FeeScheduleType.HOSPITAL_INPATIENT &&
    selectedHospital !== null &&
    ippsScenario !== "" &&
    hasIppsStdAmountsFile;

  const tableTypeForQuery = selectedHospital?.derivedTable || "";

  const { data: standardizedAmountsData } = useGetIppsStandardizedAmountsQuery(
    {
      year,
      tableType: tableTypeForQuery,
      scenario: ippsScenario,
      ...(selectedHospital?.providerNumber && {
        providerNumber: selectedHospital.providerNumber,
      }),
    },
    { skip: !shouldFetchStandardizedAmounts }
  );
  const standardizedAmounts = standardizedAmountsData?.data;

  const yearOptions = useMemo(() => getYearOptions(), []);

  const isMissingSourceFiles = useMemo(() => {
    if (!sourceDataStatus) return true;
    const requiredSources = getRequiredSourcesForType(type, includedSources);
    if (requiredSources.length === 0) return false;
    return requiredSources.some((key) => {
      const yearInfoArray = sourceDataStatus[key];
      if (!yearInfoArray || yearInfoArray.length === 0) return true;
      return !yearInfoArray.some((info) => info.year === year);
    });
  }, [sourceDataStatus, year, type, includedSources]);

  const getMissingSourceNames = useCallback(() => {
    if (!sourceDataStatus) return [];
    const requiredSources = getRequiredSourcesForType(type, includedSources);
    const missing: string[] = [];
    const sourceLabels: Partial<Record<keyof ISourceDataStatus, string>> = {
      addendumB: "Addendum B",
      addendumAA: "Addendum AA",
      clfs: "CLFS",
      asp: "ASP",
      dmepos: "DMEPOS",
      pprvu: "PPRVU",
      gpci: "GPCI Addendum E (Locality Data)",
      ipps: "IPPS Table 5 FR (MS-DRG Data)",
      ippsStdAmounts: "IPPS Tables 1A-1E (Standardized Amounts)",
      ippsImpact: "IPPS Impact File (Hospital Data)",
      ippsPsfInpatient: "IPPS PSF Inpatient (CMS API)",
      ascWageIndex: "ASC Wage Index",
    };
    requiredSources.forEach((key) => {
      const yearInfoArray = sourceDataStatus[key];
      if (
        !yearInfoArray ||
        yearInfoArray.length === 0 ||
        !yearInfoArray.some((info) => info.year === year)
      ) {
        const label = sourceLabels[key];
        if (label) {
          missing.push(label);
        }
      }
    });
    return missing;
  }, [sourceDataStatus, year, type, includedSources]);

  const resetForm = useCallback(() => {
    setDisplayName("");
    setDescription("");
    setYear(new Date().getFullYear());
    setType(FeeScheduleType.HOSPITAL_OPPS_NATIONAL);
    setApplySequester(false);
    setWageIndex(null);
    setGaf(1.0);
    setIppsScenario("quality_ehr");
    setHospitalSearchQuery("");
    setSelectedHospital(null);
    setShowHospitalSearch(false);
    setGpciPw(1.0);
    setGpciPe(1.0);
    setGpciMp(1.0);
    setDmeposState("KS_NR");
    setDmeposPercent(100);
    setAspPercent(110);
    setClfsPercent(100);
    setPprvuPercent(100);
    setIncludedSources(OPPS_SOURCE_DEFAULTS);
    setSelectedRuleIds([]);
    setIppsTchop(0);
    setIppsTchcp(0);
    setIppsDshopp(0);
    setIppsDshcpp(0);
    setIppsUcpPerClaim(0);
    // Reset CMS Inpatient fields
    setCmsProviderCcn("");
    setCmsPsfOverrides({});
    setCmsToggles(DEFAULT_CMS_TOGGLES);
    // Reset ASC fields
    setAscState("");
    setAscCountySearchQuery("");
    setSelectedAscEntry(null);
    setShowAscSearch(false);
  }, []);

  const handleClose = () => {
    if (!isCreating) {
      resetForm();
      onHide();
    }
  };

  // Handle hospital selection from IPPS Impact File search
  const handleHospitalSelect = useCallback(
    (hospital: IIppsImpactHospital) => {
      setSelectedHospital(hospital);
      setHospitalSearchQuery(hospital.name);
      setShowHospitalSearch(false);

      // Auto-fill values based on fee schedule type
      setWageIndex(hospital.wageIndex);

      if (type === FeeScheduleType.HOSPITAL_INPATIENT) {
        setGaf(hospital.gaf);
        setIppsScenario(hospital.derivedScenario);
        setIppsTchop(hospital.tchop ?? 0);
        setIppsTchcp(hospital.tchcp ?? 0);
        setIppsDshopp(hospital.dshopp ?? 0);
        setIppsDshcpp(hospital.dshcpp ?? 0);
        setIppsUcpPerClaim(hospital.ucpPerClaim ?? 0);
      }
    },
    [type]
  );

  const handleClearHospital = useCallback(() => {
    setSelectedHospital(null);
    setHospitalSearchQuery("");
    setShowHospitalSearch(false);
    setIppsTchop(0);
    setIppsTchcp(0);
    setIppsDshopp(0);
    setIppsDshcpp(0);
    setIppsUcpPerClaim(0);
  }, []);

  // Handle locality selection from GPCI search
  const handleLocalitySelect = useCallback((locality: IGpciLocality) => {
    setSelectedLocality(locality);
    setLocalitySearchQuery(locality.localityName);
    setShowLocalitySearch(false);

    // Auto-fill all three GPCI values
    setGpciPw(locality.pwGpci);
    setGpciPe(locality.peGpci);
    setGpciMp(locality.mpGpci);
  }, []);

  const handleClearLocality = useCallback(() => {
    setSelectedLocality(null);
    setLocalitySearchQuery("");
    setShowLocalitySearch(false);
  }, []);

  // Handle ASC wage index entry selection
  const handleAscEntrySelect = useCallback((entry: IASCWageIndexEntry) => {
    setSelectedAscEntry(entry);
    setAscCountySearchQuery(entry.countyName);
    setShowAscSearch(false);

    // Auto-fill wage index
    setWageIndex(entry.wageIndex);
  }, []);

  const handleClearAscEntry = useCallback(() => {
    setSelectedAscEntry(null);
    setAscCountySearchQuery("");
    setAscState("");
    setShowAscSearch(false);
  }, []);

  const isHospitalInpatientValid =
    type !== FeeScheduleType.HOSPITAL_INPATIENT || wageIndex !== null;

  const isHospitalInpatientCmsValid =
    type !== FeeScheduleType.HOSPITAL_INPATIENT_CMS ||
    (cmsProviderCcn.trim().length > 0 && cmsQuarter !== null);

  const createDisplayNameConflict = useMemo(
    () =>
      displayNameLabelTakenByAnotherLogicalFile(
        existingBaseRateFiles,
        displayName
      ),
    [existingBaseRateFiles, displayName]
  );

  const isFormValid =
    displayName.trim() &&
    year &&
    !isMissingSourceFiles &&
    atLeastOneSourceSelected &&
    isHospitalInpatientValid &&
    isHospitalInpatientCmsValid &&
    !createDisplayNameConflict;

  const handleCreate = async () => {
    if (!isFormValid) {
      return;
    }

    const dup = displayNameLabelTakenByAnotherLogicalFile(
      existingBaseRateFiles,
      displayName
    );
    if (dup) {
      toastElement?.show({
        severity: "error",
        summary: "Display name already in use",
        detail: `Another base rate file in this account already uses this display name (file: ${dup.fileName}). Choose a distinct label.`,
      });
      return;
    }

    try {
      const payload: Parameters<typeof generateBaseRateFile>[0] = {
        displayName: displayName.trim(),
        year,
        type,
        applySequester,
        ...(description.trim() && { description: description.trim() }),
        ...(selectedRuleIds.length > 0 && { selectedRuleIds }),
      };

      // Add type-specific fields
      switch (type) {
        case FeeScheduleType.HOSPITAL_INPATIENT:
          if (wageIndex !== null) payload.wageIndex = wageIndex;
          if (gaf !== null) payload.gaf = gaf;
          payload.ippsScenario = ippsScenario;
          payload.ippsTchop = ippsTchop;
          payload.ippsTchcp = ippsTchcp;
          payload.ippsDshopp = ippsDshopp;
          payload.ippsDshcpp = ippsDshcpp;
          payload.ippsUcpPerClaim = ippsUcpPerClaim;
          if (selectedHospital?.providerNumber) {
            payload.ippsImpactProviderNumber = selectedHospital.providerNumber;
          }
          break;
        case FeeScheduleType.HOSPITAL_INPATIENT_CMS:
          payload.cmsProviderCcn = cmsProviderCcn.trim();
          if (cmsQuarter !== null) {
            payload.cmsQuarter = cmsQuarter;
          }
          payload.cmsToggles = cmsToggles;
          if (Object.keys(cmsPsfOverrides).length > 0) {
            payload.cmsPsfOverrides = cmsPsfOverrides;
          }
          break;
        case FeeScheduleType.ASC:
          if (wageIndex !== null) {
            payload.wageIndex = wageIndex;
          }
          break;
        case FeeScheduleType.PHYSICIAN_FACILITY:
        case FeeScheduleType.PHYSICIAN_NON_FACILITY:
          if (gpciPw !== null) payload.gpciPw = gpciPw;
          if (gpciPe !== null) payload.gpciPe = gpciPe;
          if (gpciMp !== null) payload.gpciMp = gpciMp;
          break;
        case FeeScheduleType.HOSPITAL_OPPS_NATIONAL:
        default: {
          const activeSourceKeys = (
            Object.keys(includedSources) as OPPSSourceKey[]
          ).filter((k) => includedSources[k]);
          payload.includedSources = activeSourceKeys;

          if (includedSources.addendumB && wageIndex !== null) {
            payload.wageIndex = wageIndex;
          }
          if (includedSources.dmepos) {
            payload.dmeposState = dmeposState;
            payload.dmeposPercent = dmeposPercent ?? undefined;
          }
          if (includedSources.asp) {
            payload.aspPercent = aspPercent ?? undefined;
          }
          if (includedSources.clfs) {
            payload.clfsPercent = clfsPercent ?? undefined;
          }
          if (includedSources.pprvu) {
            payload.pprvuPercent = pprvuPercent ?? undefined;
          }
          break;
        }
      }
      await generateBaseRateFile(payload).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success",
        detail: "Base rate file created successfully",
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Failed to create base rate file:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to create base rate file",
      });
    }
  };

  // Helper to render hospital search section
  const renderHospitalSearch = (isInpatient: boolean) => (
    <>
      <SectionTitle>Hospital Search</SectionTitle>
      {!hasIppsImpactFile && (
        <div className="p-2 mb-2 border-round surface-ground text-sm text-700">
          <i className="pi pi-info-circle mr-2" />
          Please upload IPPS Impact File for year {year} first to enable
          hospital search.
        </div>
      )}
      <div className="field">
        <div className="p-inputgroup">
          <InputText
            id="hospitalSearch"
            value={hospitalSearchQuery}
            onChange={(e) => {
              setHospitalSearchQuery(e.target.value);
              setShowHospitalSearch(true);
              if (e.target.value === "") {
                setSelectedHospital(null);
              }
            }}
            placeholder="Type hospital name (min 2 chars)"
            className="w-full"
            disabled={!hasIppsImpactFile}
          />
          {selectedHospital && (
            <Button
              icon="pi pi-times"
              className="p-button-danger"
              onClick={handleClearHospital}
              tooltip="Clear selection"
            />
          )}
        </div>
        {showHospitalSearch &&
          hasIppsImpactFile &&
          hospitalSearchQuery.length >= 2 && (
            <div
              className="border-1 border-round surface-border mt-1"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {hospitalSearchResults.length === 0 ? (
                <div className="p-2 text-sm text-500">
                  No hospitals found. Try a different search term or enter
                  values manually.
                </div>
              ) : (
                hospitalSearchResults.map((hospital) => (
                  <div
                    key={hospital.providerNumber}
                    className="p-2 cursor-pointer hover:surface-ground"
                    onClick={() => handleHospitalSelect(hospital)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleHospitalSelect(hospital);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="font-medium">{hospital.name}</div>
                    <div className="text-xs text-500">
                      WI: {hospital.wageIndex.toFixed(4)}
                      {isInpatient && ` | GAF: ${hospital.gaf.toFixed(4)}`}
                      {isInpatient && ` | Table ${hospital.derivedTable}`}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        {selectedHospital && (
          <div className="mt-2 p-2 border-round surface-ground text-sm">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-check-circle text-green-500" />
              <span className="font-medium">{selectedHospital.name}</span>
            </div>
            {isInpatient && (
              <div className="text-xs text-500 mt-1 ml-4">
                Table {selectedHospital.derivedTable} | Scenario:{" "}
                {IPPS_SCENARIO_OPTIONS.find(
                  (o) => o.value === selectedHospital.derivedScenario
                )?.label || selectedHospital.derivedScenario}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderTypeSpecificFields = () => {
    switch (type) {
      case FeeScheduleType.HOSPITAL_INPATIENT_CMS:
        return (
          <HospitalInpatientCmsFields
            year={year}
            quarter={cmsQuarter ?? 0}
            onQuarterChange={setCmsQuarter}
            availableQuarters={availableQuarters}
            providerCcn={cmsProviderCcn}
            onProviderCcnChange={setCmsProviderCcn}
            toggles={cmsToggles}
            onTogglesChange={setCmsToggles}
            overrides={cmsPsfOverrides}
            onOverridesChange={setCmsPsfOverrides}
          />
        );
      case FeeScheduleType.HOSPITAL_INPATIENT:
        return (
          <>
            {renderHospitalSearch(true)}
            {!hasIppsStdAmountsFile && (
              <div className="p-2 mb-2 border-round surface-ground text-sm text-700">
                <i className="pi pi-info-circle mr-2" />
                Please upload IPPS Tables 1A-1E (Standardized Amounts) for year{" "}
                {year} to see standardized amounts preview.
              </div>
            )}
            <SectionTitle>Generation Parameters</SectionTitle>
            <FieldRow>
              <div className="field">
                <Label htmlFor="wageIndex">Wage Index *</Label>
                <InputNumber
                  id="wageIndex"
                  value={wageIndex}
                  onValueChange={(e) => setWageIndex(e.value ?? null)}
                  placeholder="Enter Wage Index (e.g., 0.9023)"
                  className="w-full"
                  minFractionDigits={4}
                  maxFractionDigits={4}
                  min={0.0001}
                  max={2}
                />
                {selectedHospital && (
                  <small className="text-xs text-500">
                    Table {selectedHospital.derivedTable}
                  </small>
                )}
              </div>
              <div className="field">
                <Label htmlFor="gaf">GAF </Label>
                <InputNumber
                  id="gaf"
                  value={gaf}
                  onValueChange={(e) => setGaf(e.value ?? null)}
                  placeholder="Default: 1.0"
                  className="w-full"
                  minFractionDigits={4}
                  maxFractionDigits={4}
                  min={0.0001}
                  max={2}
                />
              </div>
            </FieldRow>

            <SectionTitle>IPPS impact factors (IME / DSH / UCP)</SectionTitle>
            <div className="p-2 mb-2 border-round surface-ground text-xs text-600">
              From IPPS Impact file when you pick a hospital; you can override.
              Percent factors apply to computed operating/capital bases per DRG;
              UCP is a flat amount per claim.
            </div>
            <FieldRow>
              <div className="field">
                <Label htmlFor="ippsTchop">TCHOP</Label>
                <InputNumber
                  id="ippsTchop"
                  value={ippsTchop}
                  onValueChange={(e) => setIppsTchop(e.value ?? 0)}
                  className="w-full"
                  min={0}
                  max={5}
                  minFractionDigits={2}
                  maxFractionDigits={6}
                />
              </div>
              <div className="field">
                <Label htmlFor="ippsTchcp">TCHCP</Label>
                <InputNumber
                  id="ippsTchcp"
                  value={ippsTchcp}
                  onValueChange={(e) => setIppsTchcp(e.value ?? 0)}
                  className="w-full"
                  min={0}
                  max={5}
                  minFractionDigits={2}
                  maxFractionDigits={6}
                />
              </div>
              <div className="field">
                <Label htmlFor="ippsDshopp">DSHOPP</Label>
                <InputNumber
                  id="ippsDshopp"
                  value={ippsDshopp}
                  onValueChange={(e) => setIppsDshopp(e.value ?? 0)}
                  className="w-full"
                  min={0}
                  max={5}
                  minFractionDigits={2}
                  maxFractionDigits={6}
                />
              </div>
            </FieldRow>
            <FieldRow>
              <div className="field">
                <Label htmlFor="ippsDshcpp">DSHCPP</Label>
                <InputNumber
                  id="ippsDshcpp"
                  value={ippsDshcpp}
                  onValueChange={(e) => setIppsDshcpp(e.value ?? 0)}
                  className="w-full"
                  min={0}
                  max={5}
                  minFractionDigits={2}
                  maxFractionDigits={6}
                />
              </div>
              <div className="field">
                <Label htmlFor="ippsUcpPerClaim">UCP per claim ($)</Label>
                <InputNumber
                  id="ippsUcpPerClaim"
                  value={ippsUcpPerClaim}
                  onValueChange={(e) => setIppsUcpPerClaim(e.value ?? 0)}
                  className="w-full"
                  min={0}
                  max={999999}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                  prefix="$"
                />
              </div>
              <div className="field" />
            </FieldRow>

            <div className="field mt-3">
              <Label htmlFor="ippsScenario">Scenario *</Label>
              <Dropdown
                id="ippsScenario"
                value={ippsScenario}
                options={IPPS_SCENARIO_OPTIONS}
                onChange={(e) => setIppsScenario(e.value)}
                placeholder="Select Scenario"
                className="w-full"
                appendTo="self"
              />
            </div>

            {standardizedAmounts && (
              <>
                <SectionTitle>Standardized Amounts</SectionTitle>
                <div className="p-3 border-round surface-ground">
                  <div className="grid">
                    <div className="col-4">
                      <div className="text-xs text-500 mb-1">
                        Labor-related Amount
                      </div>
                      <div className="text-lg font-medium text-primary">
                        ${standardizedAmounts.laborAmount.toFixed(2)}
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-xs text-500 mb-1">
                        Non-labor-related Amount
                      </div>
                      <div className="text-lg font-medium text-primary">
                        ${standardizedAmounts.nonLaborAmount.toFixed(2)}
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-xs text-500 mb-1">
                        Capital Base (reference)
                      </div>
                      <div className="text-lg font-medium">
                        ${standardizedAmounts.capitalBase.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-500">
                    Based on Table {standardizedAmounts.tableType} with
                    scenario:{" "}
                    {
                      IPPS_SCENARIO_OPTIONS.find(
                        (o) => o.value === standardizedAmounts.scenario
                      )?.label
                    }
                  </div>
                </div>
              </>
            )}
          </>
        );

      case FeeScheduleType.ASC:
        return (
          <>
            <SectionTitle>County Search (Optional)</SectionTitle>
            {!hasAscWageIndexFile && (
              <div className="p-2 mb-2 border-round surface-ground text-sm text-700">
                <i className="pi pi-info-circle mr-2" />
                Please upload ASC Wage Index file for year {year} first to
                enable county search.
              </div>
            )}
            <div className="field">
              <Label htmlFor="ascState">State *</Label>
              <Dropdown
                id="ascState"
                value={ascState}
                options={US_STATE_OPTIONS}
                onChange={(e) => {
                  setAscState(e.value ?? "");
                  setSelectedAscEntry(null);
                  setAscCountySearchQuery("");
                }}
                placeholder="Select State"
                className="w-full"
                filter
                filterPlaceholder="Search state..."
                showClear
                disabled={!hasAscWageIndexFile}
              />
            </div>
            <div className="field">
              <Label htmlFor="ascCountySearch">County or FIPS Code</Label>
              <div className="p-inputgroup">
                <InputText
                  id="ascCountySearch"
                  value={ascCountySearchQuery}
                  onChange={(e) => {
                    setAscCountySearchQuery(e.target.value);
                    setShowAscSearch(true);
                    if (e.target.value === "") {
                      setSelectedAscEntry(null);
                    }
                  }}
                  placeholder="Type county name or FIPS (min 2 chars)"
                  className="w-full"
                  disabled={!hasAscWageIndexFile || !ascState}
                />
                {selectedAscEntry && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={handleClearAscEntry}
                    tooltip="Clear selection"
                  />
                )}
              </div>
              {showAscSearch &&
                hasAscWageIndexFile &&
                ascState &&
                ascCountySearchQuery.length >= 2 && (
                  <div
                    className="border-1 border-round surface-border mt-1"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {ascSearchResults.length === 0 ? (
                      <div className="p-2 text-sm text-500">
                        No counties found. Try a different search term or enter
                        wage index manually.
                      </div>
                    ) : (
                      ascSearchResults.map((entry) => (
                        <div
                          key={`${entry.state}-${entry.countyFips}`}
                          className="p-2 cursor-pointer hover:surface-ground"
                          onClick={() => handleAscEntrySelect(entry)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAscEntrySelect(entry);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="font-medium">{entry.countyName}</div>
                          <div className="text-xs text-500">
                            FIPS: {entry.countyFips} | CBSA: {entry.cbsa} | WI:{" "}
                            {entry.wageIndex.toFixed(4)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              {selectedAscEntry && (
                <div className="mt-2 p-2 border-round surface-ground text-sm">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-check-circle text-green-500" />
                    <span className="font-medium">
                      {selectedAscEntry.countyName}
                    </span>
                    <span className="text-xs text-500">
                      (FIPS: {selectedAscEntry.countyFips})
                    </span>
                  </div>
                </div>
              )}
            </div>

            <SectionTitle>Wage Index</SectionTitle>
            <div className="field">
              <InputNumber
                id="wageIndex"
                value={wageIndex}
                onValueChange={(e) => setWageIndex(e.value ?? null)}
                placeholder="Default: 1.0"
                className="w-full"
                minFractionDigits={4}
                maxFractionDigits={4}
                min={0.0001}
                max={2}
              />
            </div>
          </>
        );

      case FeeScheduleType.PHYSICIAN_FACILITY:
      case FeeScheduleType.PHYSICIAN_NON_FACILITY:
        return (
          <>
            <SectionTitle>Locality Search (Optional)</SectionTitle>
            {!hasGpciFile && (
              <div className="p-2 mb-2 border-round surface-ground text-sm text-700">
                <i className="pi pi-info-circle mr-2" />
                Please upload GPCI file for year {year} first to enable locality
                search.
              </div>
            )}
            <div className="field">
              <div className="p-inputgroup">
                <InputText
                  value={localitySearchQuery}
                  onChange={(e) => {
                    setLocalitySearchQuery(e.target.value);
                    setShowLocalitySearch(true);
                    if (e.target.value === "") {
                      setSelectedLocality(null);
                    }
                  }}
                  placeholder="Type locality name (min 2 chars)"
                  className="w-full"
                  disabled={!hasGpciFile}
                />
                {selectedLocality && (
                  <Button
                    icon="pi pi-times"
                    className="p-button-danger"
                    onClick={handleClearLocality}
                    tooltip="Clear selection"
                  />
                )}
              </div>
              {showLocalitySearch &&
                hasGpciFile &&
                localitySearchQuery.length >= 2 && (
                  <div
                    className="border-1 border-round surface-border mt-1"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {localitySearchResults.length === 0 ? (
                      <div className="p-2 text-sm text-500">
                        No localities found. Try a different search term or
                        enter GPCI values manually.
                      </div>
                    ) : (
                      localitySearchResults.map((locality) => (
                        <div
                          key={locality.localityNumber}
                          className="p-2 cursor-pointer hover:surface-ground"
                          onClick={() => handleLocalitySelect(locality)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleLocalitySelect(locality);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="font-medium">
                            {locality.localityName}
                          </div>
                          <div className="text-xs text-500">
                            {locality.state} | PW: {locality.pwGpci.toFixed(3)}{" "}
                            | PE: {locality.peGpci.toFixed(3)} | MP:{" "}
                            {locality.mpGpci.toFixed(3)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              {selectedLocality && (
                <div className="mt-2 p-2 border-round surface-ground text-sm">
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-check-circle text-green-500" />
                    <span className="font-medium">
                      {selectedLocality.localityName}
                    </span>
                    <span className="text-xs text-500">
                      ({selectedLocality.state})
                    </span>
                  </div>
                </div>
              )}
            </div>

            <SectionTitle>GPCI (Geographic Practice Cost Indices)</SectionTitle>
            <FieldRow>
              <div className="field">
                <Label htmlFor="gpciPw">Work (PW)</Label>
                <InputNumber
                  id="gpciPw"
                  value={gpciPw}
                  onValueChange={(e) => setGpciPw(e.value ?? null)}
                  placeholder="Default: 1.0"
                  className="w-full"
                  minFractionDigits={4}
                  maxFractionDigits={4}
                  min={0.0001}
                  max={5}
                />
              </div>
              <div className="field">
                <Label htmlFor="gpciPe">Practice Expense (PE)</Label>
                <InputNumber
                  id="gpciPe"
                  value={gpciPe}
                  onValueChange={(e) => setGpciPe(e.value ?? null)}
                  placeholder="Default: 1.0"
                  className="w-full"
                  minFractionDigits={4}
                  maxFractionDigits={4}
                  min={0.0001}
                  max={5}
                />
              </div>
              <div className="field">
                <Label htmlFor="gpciMp">Malpractice (MP)</Label>
                <InputNumber
                  id="gpciMp"
                  value={gpciMp}
                  onValueChange={(e) => setGpciMp(e.value ?? null)}
                  placeholder="Default: 1.0"
                  className="w-full"
                  minFractionDigits={4}
                  maxFractionDigits={4}
                  min={0.0001}
                  max={5}
                />
              </div>
            </FieldRow>
          </>
        );

      case FeeScheduleType.HOSPITAL_OPPS_NATIONAL:
      default:
        return (
          <>
            {renderHospitalSearch(false)}
            <SectionTitle>Source Configuration</SectionTitle>
            <div className="field">
              <div className="flex align-items-center gap-2 mb-1">
                <Checkbox
                  inputId="includeAddendumB"
                  checked={includedSources.addendumB}
                  onChange={() => toggleSource("addendumB")}
                />
                <Label htmlFor="includeAddendumB" className="mb-0">
                  Addendum B Wage Index
                </Label>
              </div>
              <InputNumber
                id="wageIndex"
                value={wageIndex}
                onValueChange={(e) => setWageIndex(e.value ?? null)}
                placeholder="Optional (e.g., 0.9011)"
                className="w-full"
                minFractionDigits={4}
                maxFractionDigits={4}
                min={0.0001}
                max={2}
                disabled={!includedSources.addendumB}
              />
            </div>
            <FieldRow>
              <div className="field">
                <div className="flex align-items-center gap-2 mb-1">
                  <Checkbox
                    inputId="includeDmepos"
                    checked={includedSources.dmepos}
                    onChange={() => toggleSource("dmepos")}
                  />
                  <Label htmlFor="includeDmepos" className="mb-0">
                    DMEPOS State
                  </Label>
                </div>
                <Dropdown
                  id="dmeposState"
                  value={dmeposState}
                  options={DMEPOS_STATE_OPTIONS}
                  onChange={(e) => setDmeposState(e.value)}
                  placeholder="Select DMEPOS State"
                  className="w-full"
                  filter
                  filterPlaceholder="Search state..."
                  showClear
                  disabled={!includedSources.dmepos}
                />
              </div>
              <div className="field">
                <Label htmlFor="dmeposPercent">DMEPOS %</Label>
                <InputNumber
                  id="dmeposPercent"
                  value={dmeposPercent}
                  onValueChange={(e) => setDmeposPercent(e.value ?? null)}
                  placeholder="Default: 100"
                  className="w-full"
                  suffix="%"
                  min={0}
                  max={500}
                  disabled={!includedSources.dmepos}
                />
              </div>
            </FieldRow>
            <FieldRow>
              <div className="field">
                <div className="flex align-items-center gap-2 mb-1">
                  <Checkbox
                    inputId="includeAsp"
                    checked={includedSources.asp}
                    onChange={() => toggleSource("asp")}
                  />
                  <Label htmlFor="includeAsp" className="mb-0">
                    ASP %
                  </Label>
                </div>
                <InputNumber
                  id="aspPercent"
                  value={aspPercent}
                  onValueChange={(e) => setAspPercent(e.value ?? null)}
                  placeholder="Default: 110"
                  className="w-full"
                  suffix="%"
                  min={0}
                  max={500}
                  disabled={!includedSources.asp}
                />
              </div>
              <div className="field">
                <div className="flex align-items-center gap-2 mb-1">
                  <Checkbox
                    inputId="includeClfs"
                    checked={includedSources.clfs}
                    onChange={() => toggleSource("clfs")}
                  />
                  <Label htmlFor="includeClfs" className="mb-0">
                    CLFS %
                  </Label>
                </div>
                <InputNumber
                  id="clfsPercent"
                  value={clfsPercent}
                  onValueChange={(e) => setClfsPercent(e.value ?? null)}
                  placeholder="Default: 100"
                  className="w-full"
                  suffix="%"
                  min={0}
                  max={500}
                  disabled={!includedSources.clfs}
                />
              </div>
              <div className="field">
                <div className="flex align-items-center gap-2 mb-1">
                  <Checkbox
                    inputId="includePprvu"
                    checked={includedSources.pprvu}
                    onChange={() => toggleSource("pprvu")}
                  />
                  <Label htmlFor="includePprvu" className="mb-0">
                    PPRVU %
                  </Label>
                </div>
                <InputNumber
                  id="pprvuPercent"
                  value={pprvuPercent}
                  onValueChange={(e) => setPprvuPercent(e.value ?? null)}
                  placeholder="Default: 100"
                  className="w-full"
                  suffix="%"
                  min={0}
                  max={500}
                  disabled={!includedSources.pprvu}
                />
              </div>
            </FieldRow>

            {!atLeastOneSourceSelected && (
              <div className="p-3 mb-3 border-round surface-ground border-left-3 border-orange-500">
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-exclamation-triangle text-orange-500" />
                  <span className="text-sm text-700">
                    Select at least one source to include.
                  </span>
                </div>
              </div>
            )}
          </>
        );
    }
  };

  const missingSourceNames = getMissingSourceNames();

  return (
    <StyledDialog
      header="Create Base Rate File"
      visible={visible}
      onHide={handleClose}
      style={{ width: "600px" }}
      closable={!isCreating}
      closeOnEscape={!isCreating}
      blockScroll
    >
      <div className="p-fluid">
        {isMissingSourceFiles && (
          <div className="p-3 mb-3 border-round surface-ground border-left-3 border-orange-500">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-exclamation-triangle text-orange-500" />
              <span className="text-sm text-700">
                Missing source files for {year}: {missingSourceNames.join(", ")}
                . Open <strong>Upload Source Files</strong> and upload or fetch
                these first.
              </span>
            </div>
          </div>
        )}

        <div className="field">
          <Label htmlFor="createType">Fee Schedule Type *</Label>
          <Dropdown
            id="createType"
            value={type}
            options={FEE_SCHEDULE_TYPE_OPTIONS}
            appendTo="self"
            onChange={(e) => {
              const newType = e.value;
              setType(newType);
              setSelectedRuleIds([]); // Clear selected rules when type changes
              if (newType === FeeScheduleType.HOSPITAL_INPATIENT) {
                setApplySequester(false);
                setWageIndex(1.0);
                setGaf(1.0);
                setIppsScenario("quality_ehr");
                setHospitalSearchQuery("");
                setSelectedHospital(null);
                setShowHospitalSearch(false);
                setIppsTchop(0);
                setIppsTchcp(0);
                setIppsDshopp(0);
                setIppsDshcpp(0);
                setIppsUcpPerClaim(0);
                // Reset ASC fields
                setAscState("");
                setAscCountySearchQuery("");
                setSelectedAscEntry(null);
                setShowAscSearch(false);
              }
              if (newType === FeeScheduleType.HOSPITAL_INPATIENT_CMS) {
                setApplySequester(false);
                setCmsProviderCcn("");
                setCmsPsfOverrides({});
                setCmsToggles(DEFAULT_CMS_TOGGLES);
              }
            }}
            placeholder="Select Type"
            className="w-full"
          />
        </div>

        <div className="field">
          <Label htmlFor="createDisplayName">Display Name *</Label>
          <InputText
            id="createDisplayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter a display name for the base rate file"
            className="w-full"
          />
          {createDisplayNameConflict && (
            <Message
              severity="error"
              className="w-full mt-2"
              text={`Display name must be unique per account among logical files. Already used by “${createDisplayNameConflict.displayName}” (${createDisplayNameConflict.fileName}).`}
            />
          )}
        </div>

        <div className="field">
          <Label htmlFor="createDescription">Description</Label>
          <InputText
            id="createDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description for the base rate file"
            className="w-full"
          />
        </div>

        <div className="field">
          <Label htmlFor="createYear">Year *</Label>
          <Dropdown
            id="createYear"
            value={year}
            options={yearOptions}
            onChange={(e) => setYear(e.value)}
            placeholder="Select Year"
            className="w-full"
            appendTo="self"
          />
        </div>

        {renderTypeSpecificFields()}

        <div className="field-checkbox mt-3 mb-3">
          <Checkbox
            inputId="applySequester"
            checked={applySequester}
            onChange={(e) => setApplySequester(e.checked ?? false)}
          />
          <label htmlFor="applySequester" className="ml-2">
            Apply Medicare Sequester
          </label>
        </div>

        <StyledMultiSelectWrapper className="field">
          <Label htmlFor="selectedRuleIds">Override Rules</Label>
          <MultiSelect
            id="selectedRuleIds"
            value={selectedRuleIds}
            options={overrideRuleOptions}
            onChange={(e) => setSelectedRuleIds(e.value)}
            placeholder="Select rules to apply"
            className="w-full"
            filter
            showClear
            display="chip"
            appendTo="self"
          />
        </StyledMultiSelectWrapper>

        <div className="flex justify-content-end gap-2 mt-4 pb-3">
          <Button
            label={isCreating ? "Creating..." : "Create"}
            icon={isCreating ? "pi pi-spin pi-spinner" : "pi pi-check"}
            onClick={handleCreate}
            disabled={isCreating || !isFormValid}
            loading={isCreating}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-outlined-light"
            onClick={handleClose}
            disabled={isCreating}
          />
        </div>
      </div>
    </StyledDialog>
  );
}

export default CreateBaseRateFileDialog;
